import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DollarSign, Plus, Trash2, XCircle } from "lucide-react";

const empty = () => ({
  currency: "usd", tax_percent: 0,
  issued_at: new Date().toISOString().slice(0, 10), due_date: "",
  notes: "", terms: "Payment due within 14 days of invoice date.",
  items: [{ description: "", quantity: 1, unit_price_cents: 0 }],
});
const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const hydrate = (r) => ({
  currency: r.currency || "usd", tax_percent: Number(r.tax_percent) || 0,
  issued_at: r.issued_at || new Date().toISOString().slice(0, 10),
  due_date: r.due_date || "", notes: r.notes || "", terms: r.terms || "",
  items: (r.items && r.items.length ? r.items : [{ description: "", quantity: 1, unit_price_cents: 0 }])
    .map((it) => ({ description: it.description || "", quantity: Number(it.quantity) || 1, unit_price_cents: Number(it.unit_price_cents) || 0 })),
});

export default function InvoicePanel({ client, recordId, onSaved, onContext }) {
  const { currentOrgId } = useAuth();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(false);
  const loadedIdRef = useRef(null);

  useEffect(() => {
    if (!recordId) { setEditing(null); setForm(empty()); loadedIdRef.current = null; return; }
    if (loadedIdRef.current === recordId) return; // avoid clobbering post-save state
    loadedIdRef.current = recordId;
    setLoading(true);
    apiFetch(`/invoices/${recordId}`).then((r) => {
      setEditing(r);
      setForm(hydrate(r));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [recordId]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price_cents) || 0), 0);
    const tax = Math.round(subtotal * (Number(form.tax_percent) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_percent]);

  const save = async () => {
    if (!client) return toast.error("Pick a client first");
    if (!form.items.length || form.items.some((it) => !it.description.trim())) return toast.error("Every line item needs a description");
    const body = {
      organization_id: currentOrgId, client_id: client.id,
      currency: form.currency, tax_percent: Number(form.tax_percent) || 0,
      issued_at: form.issued_at, due_date: form.due_date || null,
      notes: form.notes || null, terms: form.terms || null,
      items: form.items.map((it, idx) => ({ description: it.description, quantity: Number(it.quantity) || 1, unit_price_cents: Math.round(Number(it.unit_price_cents) || 0), position: idx })),
    };
    try {
      let r;
      if (editing) r = await apiFetch(`/invoices/${editing.id}`, { method: "PATCH", body: JSON.stringify(body) });
      else r = await apiFetch("/invoices", { method: "POST", body: JSON.stringify(body) });
      if (r?.id) {
        setEditing(r);
        setForm(hydrate(r));
        loadedIdRef.current = r.id;
      }
      toast.success(editing ? "Invoice updated" : "Invoice created");
      onSaved && onSaved({ kind: "invoice", id: r?.id || editing?.id });
    } catch (e) { toast.error(e.message); }
  };

  const sendInvoice = async () => {
    if (!editing) return toast.error("Save the invoice first");
    try {
      const r = await apiFetch(`/invoices/${editing.id}/send`, { method: "POST", body: JSON.stringify({ send_email: true }) });
      if (r.email_sent) toast.success("Invoice emailed to client");
      else if (r.portal_link) { try { await navigator.clipboard.writeText(r.portal_link); } catch {} toast.success("Marked sent — pay link copied"); }
      onSaved && onSaved({ kind: "invoice", id: editing.id });
    } catch (e) { toast.error(e.message); }
  };
  const markPaid = async () => {
    if (!editing) return;
    if (!window.confirm(`Mark ${editing.invoice_number} paid?`)) return;
    try { await apiFetch(`/invoices/${editing.id}/mark-paid`, { method: "POST", body: "{}" }); toast.success("Marked paid"); onSaved && onSaved({ kind: "invoice", id: editing.id }); } catch (e) { toast.error(e.message); }
  };

  const voidInvoice = async () => {
    if (!editing) return;
    if (!window.confirm(`Void ${editing.invoice_number}? The invoice will no longer be payable and — if it had a Stripe pay link — that link will be expired.`)) return;
    try {
      await apiFetch(`/invoices/${editing.id}/void`, { method: "POST" });
      toast.success("Invoice voided");
      onSaved && onSaved({ kind: "invoice", id: editing.id });
    } catch (e) { toast.error(e.message); }
  };
  const removeInvoice = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete invoice ${editing.invoice_number}? Only draft/void invoices can be deleted; this cannot be undone.`)) return;
    try {
      await apiFetch(`/invoices/${editing.id}`, { method: "DELETE" });
      toast.success("Invoice deleted");
      onSaved && onSaved({ kind: "invoice", id: editing.id, deleted: true });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    const isDraft = !editing || editing?.status === "draft";
    const isVoid = editing?.status === "void";
    const isPaid = editing?.status === "paid";
    const canDelete = editing && (isDraft || isVoid);
    const canVoid = editing && !isPaid && !isVoid && !isDraft; // sent / overdue
    onContext && onContext({
      client, status: editing?.status || "draft", editing,
      actions: [
        { key: "save", label: editing ? "Update" : "Create", variant: "primary", testid: "inv-save", onClick: save },
        ...(editing && !isPaid && !isVoid ? [
          { key: "send", label: "Send to client", variant: "outline", testid: "inv-send", onClick: sendInvoice },
          { key: "paid", label: "Mark paid", variant: "outline", testid: "inv-paid", onClick: markPaid },
        ] : []),
        ...(canVoid ? [
          { key: "void", label: "Void", icon: XCircle, variant: "outline", testid: "inv-void", onClick: voidInvoice },
        ] : []),
        ...(canDelete ? [
          { key: "delete", label: "Delete", icon: Trash2, variant: "outline", testid: "inv-delete", onClick: removeInvoice },
        ] : []),
      ],
    });
    /* eslint-disable-next-line */
  }, [form, editing]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price_cents: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, key, val) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it) });

  if (loading) return <div className="text-center py-16 text-sm text-[#57534E]">Loading invoice…</div>;

  return (
    <div className="space-y-4" data-testid="workspace-invoice">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#57534E] flex items-center gap-1.5"><DollarSign size={13} /> Invoice</div>
        <div className="text-lg font-semibold font-display">{editing?.invoice_number || "New Invoice"} {client && <span className="text-sm text-[#57534E]">— {client.first_name} {client.last_name}</span>}</div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Issue date"><Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} /></Field>
          <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
          <Field label="Tax %"><Input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} /></Field>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
            <tr>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2 w-20">Qty</th>
              <th className="text-right px-3 py-2 w-32">Unit price</th>
              <th className="text-right px-3 py-2 w-32">Total</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i} className="border-t border-[#E8E2D9]">
                <td className="px-3 py-2"><Input value={it.description} data-testid={`inv-item-${i}-desc`} onChange={(e) => updateItem(i, "description", e.target.value)} /></td>
                <td className="px-3 py-2"><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="text-right" /></td>
                <td className="px-3 py-2"><Input type="number" step="0.01" value={(it.unit_price_cents / 100) || ""} onChange={(e) => updateItem(i, "unit_price_cents", Math.round(Number(e.target.value || 0) * 100))} className="text-right" /></td>
                <td className="px-3 py-2 text-right font-medium">{money((it.quantity || 0) * (it.unit_price_cents || 0))}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => removeItem(i)} disabled={form.items.length <= 1} className="text-[#B02222] disabled:opacity-30" title="Remove"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-3 border-t border-[#E8E2D9] flex items-center justify-between bg-[#FAF8F5]">
          <Button size="sm" variant="outline" className="rounded-full" onClick={addItem} data-testid="inv-add-item"><Plus size={12} className="mr-1" /> Add line item</Button>
          <div className="text-sm space-y-1 text-right">
            <div className="text-[#57534E]">Subtotal: <span className="font-medium text-[#1C1917]">{money(totals.subtotal)}</span></div>
            <div className="text-[#57534E]">Tax: <span className="font-medium text-[#1C1917]">{money(totals.tax)}</span></div>
            <div className="text-base font-semibold">Total: {money(totals.total)}</div>
          </div>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <Field label="Terms"><Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></Field>
      </div>
    </div>
  );
}
function Field({ label, children }) { return <div><Label className="text-sm">{label}</Label><div className="mt-1">{children}</div></div>; }
